import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orcamentosApi, fetchCambioUsdBrl } from '@/api/endpoints/orcamentos';
import type { ProdutoInput, FreteItem, OrcamentoSalvoInput } from '@/api/endpoints/orcamentos';

// ----- Catálogo de produtos -----

export const useProdutos = () =>
  useQuery({
    queryKey: ['orcamentos-produtos'],
    queryFn: orcamentosApi.listProdutos,
    staleTime: 1000 * 30,
    retry: 1,
  });

export const useCreateProduto = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ProdutoInput) => orcamentosApi.createProduto(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orcamentos-produtos'] }),
  });
};

export const useUpdateProduto = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ProdutoInput> }) =>
      orcamentosApi.updateProduto(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orcamentos-produtos'] }),
  });
};

export const useDeleteProduto = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => orcamentosApi.deleteProduto(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orcamentos-produtos'] }),
  });
};

// ----- Câmbio ao vivo -----

export const useCambio = () =>
  useQuery({
    queryKey: ['orcamentos-cambio'],
    queryFn: fetchCambioUsdBrl,
    staleTime: 1000 * 60 * 5, // 5 min
    retry: 1,
  });

// ----- Cotação de frete -----

export const useCotarFrete = () =>
  useMutation({
    mutationFn: ({
      cepDestino,
      itens,
      cepOrigem,
    }: {
      cepDestino: string;
      itens: FreteItem[];
      cepOrigem?: string;
    }) => orcamentosApi.cotarFrete(cepDestino, itens, cepOrigem),
  });

// ----- Orçamentos salvos (histórico) -----

export const useOrcamentosSalvos = () =>
  useQuery({
    queryKey: ['orcamentos-salvos'],
    queryFn: orcamentosApi.listSalvos,
    staleTime: 1000 * 15,
    retry: 1,
  });

export const useSalvarOrcamento = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: OrcamentoSalvoInput) => orcamentosApi.createSalvo(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orcamentos-salvos'] }),
  });
};

export const useAtualizarOrcamento = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: OrcamentoSalvoInput }) =>
      orcamentosApi.updateSalvo(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orcamentos-salvos'] }),
  });
};

export const useDeletarOrcamento = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => orcamentosApi.deleteSalvo(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orcamentos-salvos'] }),
  });
};
